import { useRef } from 'react';

import { Icon, Modal } from '@repo/ui';
import { useYouTubePlayer } from 'hooks/useYouTubePlayer';

import { createWatchTimer, WatchSummary } from './watchTimer';
import styles from './MetricVideo.module.css';

interface MetricVideoPlayerProps {
  youtubeId: string;
  isOpen: boolean;
  onClose: (summary: WatchSummary) => void;
}

// Mounted only while the modal is open, so every open is a fresh player that
// starts from 0:00 and the host node exists before the iframe is attached.
const PlayerSurface = ({
  youtubeId,
  timer,
}: {
  youtubeId: string;
  timer: ReturnType<typeof createWatchTimer>;
}) => {
  const hostRef = useRef<HTMLDivElement>(null);
  useYouTubePlayer({
    videoId: youtubeId,
    hostRef,
    loop: false,
    onStateChange: timer.onStateChange,
  });

  return <div ref={hostRef} className={styles.playerHost} />;
};

export const MetricVideoPlayer = ({
  youtubeId,
  isOpen,
  onClose,
}: MetricVideoPlayerProps) => {
  const timerRef = useRef(createWatchTimer());

  const handleClose = () => {
    onClose(timerRef.current.stop());
    timerRef.current = createWatchTimer();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      closeOnBackdropClick={false}
      hasBodyPadding={false}
      className={styles.playerDialog}
    >
      <div className={styles.player}>
        <PlayerSurface youtubeId={youtubeId} timer={timerRef.current} />
        <button
          type='button'
          className={styles.closeButton}
          onClick={handleClose}
          aria-label='Close video'
        >
          <Icon name='closeSm' size='m' />
        </button>
      </div>
    </Modal>
  );
};

export default MetricVideoPlayer;
